import type {
  JournalEntry,
  JournalEntryKind,
  JournalEntryRealization,
  JournalEntryRevision,
} from "@/types/api";
import { request } from "./transport";

export interface JournalEntryInput {
  position_id?: string | null;
  saved_strategy_id?: string | null;
  note_id?: string | null;
  kind?: JournalEntryKind;
  category?: string;
  occurred_at?: string;
  source?: string;
  template_key?: string | null;
  template_version?: number | null;
  title?: string | null;
  body?: string;
  ticker?: string | null;
  tags?: string[];
  pnl_amount?: number | null;
  currency?: string;
  return_rate?: number | null;
  return_basis_type?: string | null;
  return_basis_amount?: number | null;
  realization?: JournalEntryRealization;
  metadata?: Record<string, unknown>;
  archived_at?: string | null;
}

export interface JournalEntriesFilter {
  linkage?: "linked" | "standalone";
  source?: string;
  strategy_kind?: string;
  kind?: JournalEntryKind;
  category?: string;
  ticker?: string;
  position_id?: string;
  saved_strategy_id?: string;
  from?: string;
  to?: string;
  before?: string;
  archived?: "include" | "only";
  realization?: JournalEntryRealization;
  q?: string;
  tags?: string[];
  limit?: number;
}

export interface JournalEntriesResponse {
  journal_entries: JournalEntry[];
  next_cursor: string | null;
  categories: string[];
}

export const journalEntries = {
  listJournalEntries: (params?: JournalEntriesFilter) => {
    const qs = new URLSearchParams();
    if (params?.linkage) qs.set("linkage", params.linkage);
    if (params?.source) qs.set("source", params.source);
    if (params?.strategy_kind) qs.set("strategy_kind", params.strategy_kind);
    if (params?.kind) qs.set("kind", params.kind);
    if (params?.category) qs.set("category", params.category);
    if (params?.ticker) qs.set("ticker", params.ticker);
    if (params?.position_id) qs.set("position_id", params.position_id);
    if (params?.saved_strategy_id)
      qs.set("saved_strategy_id", params.saved_strategy_id);
    if (params?.from) qs.set("from", params.from);
    if (params?.to) qs.set("to", params.to);
    if (params?.before) qs.set("before", params.before);
    if (params?.archived) qs.set("archived", params.archived);
    if (params?.realization) qs.set("realization", params.realization);
    if (params?.q) qs.set("q", params.q);
    if (params?.tags?.length) qs.set("tags", params.tags.join(","));
    if (params?.limit) qs.set("limit", String(params.limit));
    const q = qs.toString();
    return request<JournalEntriesResponse>(
      `/journal_entries${q ? `?${q}` : ""}`,
    );
  },

  getJournalEntry: (id: string) =>
    request<{ journal_entry: JournalEntry }>(`/journal_entries/${id}`),

  createJournalEntry: (input: JournalEntryInput) =>
    request<{ journal_entry: JournalEntry }>("/journal_entries", {
      method: "POST",
      body: JSON.stringify({ journal_entry: input }),
    }),

  updateJournalEntry: (id: string, patch: JournalEntryInput) =>
    request<{ journal_entry: JournalEntry }>(`/journal_entries/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ journal_entry: patch }),
    }),

  listJournalEntryRevisions: (id: string) =>
    request<{ journal_entry_revisions: JournalEntryRevision[] }>(
      `/journal_entries/${id}/revisions`,
    ),

  restoreJournalEntryRevision: (entryId: string, revisionId: string) =>
    request<{ journal_entry: JournalEntry }>(
      `/journal_entries/${entryId}/revisions/${revisionId}/restore`,
      { method: "POST" },
    ),

  deleteJournalEntry: (id: string) =>
    request<{ ok: true }>(`/journal_entries/${id}`, { method: "DELETE" }),
};
